import { useCallback, useEffect, useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { testIds } from '@/lib/test-ids'
import { fetchWithAuth } from '@/lib/api/core'
import type { AsyncActivityState } from '@/components/ui/async-activity-strip'
import { cn } from '@/lib/utils'

/** Formats an ISO timestamp as a compact relative age (e.g. "3h ago"). */
export function formatGdocRelativeTime(iso?: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const diff = Date.now() - date.getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

type DriveComment = {
  resolved?: boolean
}

/**
 * Compact Drive link meta for the document bottom status bar / info tooltip.
 * Header: Drive icon + "Synced with Google Drive"
 * Detail: comments | last synced (+ Keep synced when on)
 */
export function GdocStatusBarMeta({
  gdocId,
  lastSynced,
  lastPushed,
  lastPulled,
  organizationId,
  keepSynced = false,
  activity = 'idle',
  activityLabel,
  className,
}: {
  gdocId: string
  lastSynced?: string | null
  lastPushed?: string | null
  lastPulled?: string | null
  organizationId?: string | null
  keepSynced?: boolean
  activity?: AsyncActivityState
  activityLabel?: string | null
  className?: string
}) {
  const [openCommentCount, setOpenCommentCount] = useState<number | null>(null)

  const loadComments = useCallback(async () => {
    try {
      const params = organizationId
        ? `?organizationId=${encodeURIComponent(organizationId)}`
        : ''
      const res = await fetchWithAuth(
        `/api/google-drive/files/${encodeURIComponent(gdocId)}/comments${params}`,
      )
      if (!res.ok) {
        setOpenCommentCount(null)
        return
      }
      const data = (await res.json()) as { comments?: DriveComment[] }
      const open = (data.comments ?? []).filter((comment) => !comment.resolved).length
      setOpenCommentCount(open)
    } catch {
      setOpenCommentCount(null)
    }
  }, [gdocId, organizationId])

  useEffect(() => {
    void loadComments()
  }, [loadComments])

  const syncTimestamp = lastSynced || lastPushed || lastPulled
  const idleSyncLabel = syncTimestamp
    ? `Synced ${formatGdocRelativeTime(syncTimestamp)}`
    : 'Not synced yet'

  const showActivity = activity !== 'idle' && Boolean(activityLabel)
  const lastSyncText = showActivity ? activityLabel : idleSyncLabel

  const lastSyncClass =
    activity === 'working'
      ? 'text-primary'
      : activity === 'success'
        ? 'text-green-700 dark:text-green-400'
        : activity === 'conflict'
          ? 'text-amber-800 dark:text-amber-400'
          : activity === 'error'
            ? 'text-destructive'
            : 'text-muted-foreground'

  return (
    <div
      className={cn('flex flex-col gap-0.5 min-w-0 text-left', className)}
      data-testid={testIds.documentViewer.gdocSyncBadge}
    >
      <div className="flex items-center gap-1.5 text-foreground">
        <GoogleDriveIcon className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate font-medium">Synced with Google Drive</span>
        {keepSynced ? (
          <span
            className="shrink-0 text-muted-foreground font-normal"
            data-testid={testIds.documentViewer.gdocKeepSyncedToggle}
            title="Keep synced is on - Maater syncs while this doc is open"
          >
            (Keep synced)
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-1.5 min-w-0 text-muted-foreground">
        {openCommentCount !== null ? (
          <span
            className="inline-flex items-center gap-1 shrink-0 text-yellow-700 dark:text-yellow-500"
            data-testid={testIds.documentViewer.gdocCommentCount}
            title={`${openCommentCount} open comment${openCommentCount !== 1 ? 's' : ''} in Google Doc`}
          >
            <MessageSquare className="h-3 w-3" />
            {openCommentCount} open comment{openCommentCount !== 1 ? 's' : ''}
          </span>
        ) : null}
        {openCommentCount !== null && lastSyncText ? (
          <span className="text-muted-foreground/50" aria-hidden>
            |
          </span>
        ) : null}
        {lastSyncText ? (
          <span
            className={cn('truncate', lastSyncClass)}
            data-testid={
              showActivity
                ? testIds.documentViewer.gdocSyncActivityStrip
                : testIds.documentViewer.gdocLastSync
            }
            role="status"
            aria-live="polite"
          >
            {lastSyncText}
          </span>
        ) : null}
      </div>
    </div>
  )
}
